import { IconButton, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { selectFeatureFlagAgentCart } from '../../redux/features/config/config.selector';
import useAISuggestionActions from '../AISuggestions/useAISuggestionActions.hooks';
import useEventListener from '../../hooks/useEventListener.hooks';
import { useReminder } from '../../contexts/ReminderContext';
import Colors from '../../utils/color';
import ConsoleCard from '../MainPageLayout/ConsoleCard';

/**
 * ACTION CENTER (left pane, bottom card).
 *
 * The co-pilot's "advise" surface, folded into one card: the AI's recommended
 * ask with Accept / Dismiss (tick / cross), and — below it — the active
 * slash-command reminder. The header (icon + title + collapse chevron) sits
 * inside the white card, above a divider.
 *
 * The recommended ask is the live AI suggestion (gated by the agent_cart flag,
 * same as the standalone AISuggestions surface); Accept / Dismiss and the
 * Alt+A / Alt+D shortcuts drive the shared useAISuggestionActions path. The
 * reminder is the live slash-command reminder (useReminder). The card always
 * renders; each section shows only when it has something to surface.
 */

const useStyles = makeStyles((theme: Theme) => ({
  // Everything below the header. Fills the card and scrolls INTERNALLY so the
  // scrollbar stays inside the card, not on the outer resizable pane. The header
  // divider is supplied by ConsoleCard; the body just adds the matching top
  // breathing room below that line.
  body: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    paddingTop: theme.spacing(1.25),
  },
  // "AI recommends you ask" label + Accept/Dismiss icons on the same line.
  recommendRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  // Match Customer Pulse's "Intent" label (readoutKey): grey, non-bold.
  recommendLabel: {
    flex: 1,
    minWidth: 0,
    fontSize: '0.66rem',
    letterSpacing: '0.03em',
    color: '#79808a',
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(0.5),
    flexShrink: 0,
  },
  okBtn: {
    backgroundColor: Colors.green,
    color: Colors.white,
    '&:hover': { backgroundColor: Colors.green },
  },
  noBtn: {
    backgroundColor: Colors.white,
    color: '#79808a',
    border: `1px solid ${Colors.alto}`,
  },
  // The AI's recommended ask, in a tinted box.
  // Match the "Welcome" intent pill's text: medium weight, 0.72rem.
  askBox: {
    backgroundColor: '#eef1fb',
    border: '1px solid #c5cae9',
    borderRadius: '6px',
    padding: theme.spacing(0.875, 1.125),
    fontSize: '0.72rem',
    fontWeight: 500,
    color: Colors.readBack,
  },
  reason: {
    fontSize: '0.77rem',
    color: Colors.mineShaft,
  },
  // Reminder block. When it follows the recommended ask, a dashed separator
  // divides them (reminderDivided); as the sole content it needs no divider.
  reminderBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
  },
  reminderDivided: {
    marginTop: theme.spacing(0.25),
    paddingTop: theme.spacing(1),
    borderTop: `1px dashed ${Colors.alto}`,
  },
  // Match Customer Pulse's "Intent" label (readoutKey): grey, non-bold.
  reminderHeader: {
    fontSize: '0.66rem',
    letterSpacing: '0.03em',
    color: '#79808a',
  },
  reminderText: {
    fontSize: '0.77rem',
    color: Colors.mineShaft,
    // Wrap long messages / unbreakable tokens instead of overflowing.
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
  },
  empty: {
    fontSize: '0.72rem',
    color: '#79808a',
    fontStyle: 'italic',
  },
}));

interface ActionCenterProps {
  // Optional controlled collapse so the parent can drive the resizable panel
  // (collapsing frees its space for the transcript). Falls back to internal
  // state (default open) when used standalone.
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const ActionCenter = ({
  open: openProp,
  onOpenChange,
}: ActionCenterProps = {}) => {
  const classes = useStyles();
  const [openState, setOpenState] = useState(true);
  const open = openProp ?? openState;
  const toggleOpen = () => {
    const next = !open;
    if (openProp === undefined) setOpenState(next);
    onOpenChange?.(next);
  };

  // The co-pilot advise loop is gated by the agent_cart flag (the same master
  // switch as the working-cart input side), matching the standalone
  // AISuggestions surface.
  const isAgentCartEnabled = useSelector(selectFeatureFlagAgentCart);
  const { activeSuggestion, accept, dismiss } = useAISuggestionActions();
  const { reminderMessage, isVisible: reminderVisible } = useReminder();

  const showAsk = isAgentCartEnabled && !!activeSuggestion;
  const showReminder = reminderVisible && !!reminderMessage;

  // Alt+A / Alt+D mirror the card's Accept / Dismiss buttons (shared path via
  // useAISuggestionActions). `event.code` is matched (not `event.key`) so macOS
  // Option-key glyphs don't break the match; capture phase so it fires even
  // while the omnibar is focused. No-ops unless the co-pilot is enabled and a
  // suggestion is showing.
  const handleKeyDown = (event: Event) => {
    const e = event as KeyboardEvent;
    if (!e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) {
      return;
    }
    if (e.code !== 'KeyA' && e.code !== 'KeyD') {
      return;
    }
    if (!isAgentCartEnabled || !activeSuggestion) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    if (e.code === 'KeyA') {
      accept(activeSuggestion);
    } else {
      dismiss(activeSuggestion);
    }
  };
  useEventListener('keydown', handleKeyDown, window, true);

  return (
    <ConsoleCard
      icon={<BoltOutlinedIcon />}
      title="Actions"
      open={open}
      onToggle={toggleOpen}
      testId="action-center"
      // Keep the body mounted (hidden) so collapse/expand is instant.
      keepBodyMounted
    >
      <div className={classes.body}>
        {showAsk && activeSuggestion && (
          <>
            <div className={classes.recommendRow}>
              <span className={classes.recommendLabel}>
                AI recommends you ask:
              </span>
              <div className={classes.actions}>
                <IconButton
                  className={classes.okBtn}
                  size="small"
                  title="Accept (Alt+A)"
                  aria-label="Accept suggestion"
                  onClick={() => accept(activeSuggestion)}
                  // Tighter padding (sx beats MUI) so the row is shorter.
                  sx={{ p: 0.5 }}
                >
                  <CheckIcon fontSize="small" />
                </IconButton>
                <IconButton
                  className={classes.noBtn}
                  size="small"
                  title="Dismiss (Alt+D)"
                  aria-label="Dismiss suggestion"
                  onClick={() => dismiss(activeSuggestion)}
                  sx={{ p: 0.5 }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </div>
            </div>
            <div className={classes.askBox}>{activeSuggestion.title}</div>
            {activeSuggestion.reason && (
              <span className={classes.reason}>{activeSuggestion.reason}</span>
            )}
          </>
        )}

        {showReminder && (
          <div
            className={`${classes.reminderBlock} ${
              showAsk ? classes.reminderDivided : ''
            }`}
          >
            <span className={classes.reminderHeader}>Reminder</span>
            <div className={classes.reminderText}>{reminderMessage}</div>
          </div>
        )}

        {!showAsk && !showReminder && (
          <span className={classes.empty}>No actions right now</span>
        )}
      </div>
    </ConsoleCard>
  );
};

export default ActionCenter;
